/* Approved scroll-frame dimensions/events; one seekable video per active scene. */
(() => {
  'use strict';
  if (window.NarwalScroll) { window.NarwalScroll.init(document); return; }
  const frames = new Map();
  const fades = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 1023px)');
  const clamp = value => Math.max(0, Math.min(1, value));
  const number = (value, fallback) => Number.isFinite(parseFloat(value)) ? parseFloat(value) : fallback;
  let scheduled = 0;

  function visit(scope, selector, callback) {
    if (scope instanceof Element && scope.matches(selector)) callback(scope);
    scope.querySelectorAll(selector).forEach(callback);
  }
  function createFrame(root) {
    if (frames.has(root)) return;
    const wrap = root.querySelector('[data-ndev-sf-canvas]');
    const canvas = wrap?.querySelector('canvas');
    const context = canvas?.getContext('2d');
    if (!wrap || !canvas || !context) return;
    const posters = [...wrap.querySelectorAll('.ndev-sf__poster')];
    const originalHeight = root.style.height;
    const events = new AbortController();
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';
    let duration = 0;
    let url = '';
    let progress = -1;
    const desiredTime = () => Math.max(0, progress) * (Number.isFinite(video.duration) ? video.duration : duration);
    let active = false;
    let near = false;
    let ready = false;
    let failed = false;
    let disposed = false;
    let seekTimer = 0;
    let lastSeek = 0;
    const interval = Math.max(16, number(wrap.dataset.ndevSfFrameInterval, 40));

    function showPoster() {
      posters.forEach(poster => poster.classList.remove('hidden'));
      root.removeAttribute('data-nwb-sf-ready');
      ready = false;
    }
    function load() {
      if (disposed || failed || motion.matches || !near || !url || video.getAttribute('src') === url) return;
      video.src = url;
      video.load();
    }
    function seek() {
      seekTimer = 0;
      if (disposed || failed || !active || motion.matches || video.readyState < 1 || video.seeking) return;
      const elapsed = performance.now() - lastSeek;
      if (elapsed < interval) { seekTimer = setTimeout(seek, interval - elapsed); return; }
      const maximum = Math.max(0, video.duration - 0.025);
      const nextTime = Math.min(maximum, desiredTime());
      if (Math.abs(video.currentTime - nextTime) < 0.012 && video.readyState >= 2) { draw(); return; }
      lastSeek = performance.now();
      try { video.currentTime = nextTime; } catch (_) { /* Metadata may still be settling. */ }
    }
    function draw() {
      if (disposed || failed || !active || motion.matches || video.seeking || video.readyState < 2) return;
      const expectedTime = Math.min(Math.max(0, video.duration - 0.025), desiredTime());
      if (Math.abs(video.currentTime - expectedTime) > 0.035) { seek(); return; }
      try {
        if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
        }
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        root.dataset.nwbSfTime = video.currentTime.toFixed(3);
        if (!ready) {
          ready = true;
          posters.forEach(poster => poster.classList.add('hidden'));
          root.setAttribute('data-nwb-sf-ready', '');
          root.dispatchEvent(new CustomEvent('ndev-sf-ready', { detail: { duration }, bubbles: true }));
        }
      } catch (_) {
        failed = true;
        showPoster();
        root.setAttribute('data-nwb-sf-fallback', '');
      }
    }
    function configure() {
      const nextUrl = mobile.matches ? (wrap.dataset.ndevSfMbUrl || wrap.dataset.ndevSfPcUrl) : wrap.dataset.ndevSfPcUrl;
      duration = mobile.matches ? number(wrap.dataset.ndevSfMbDuration, number(wrap.dataset.ndevSfPcDuration, 0)) : number(wrap.dataset.ndevSfPcDuration, 0);
      const vhPerSecond = Math.max(1, number(root.dataset.ndevSfVhPerSecond, 100));
      if (motion.matches || !duration) {
        root.style.height = originalHeight;
        video.pause();
        clearTimeout(seekTimer);
        showPoster();
      } else { root.style.height = `${Math.ceil(duration * vhPerSecond)}vh`; }
      root.toggleAttribute('data-nwb-sf-static', motion.matches);
      if (url !== nextUrl) {
        url = nextUrl || '';
        failed = false;
        video.pause();
        video.removeAttribute('src');
        video.load();
        showPoster();
      }
      load();
      update();
    }
    function update() {
      if (disposed) return;
      const rect = root.getBoundingClientRect();
      active = rect.bottom > 0 && rect.top < innerHeight && !document.hidden;
      const nextProgress = clamp(-rect.top / Math.max(1, root.offsetHeight - innerHeight));
      if (Math.abs(progress - nextProgress) > 0.0005) {
        progress = nextProgress;
        root.dataset.nwbSfProgress = progress.toFixed(3);
        root.dispatchEvent(new CustomEvent('ndev-sf-progress', { detail: { progress, percent: Math.round(progress * 100), duration }, bubbles: true }));
      }
      if (!active || motion.matches) { clearTimeout(seekTimer); video.pause(); return; }
      load();
      seek();
    }
    video.addEventListener('loadedmetadata', seek, { signal: events.signal });
    video.addEventListener('loadeddata', draw, { signal: events.signal });
    video.addEventListener('seeked', () => { draw(); seek(); }, { signal: events.signal });
    video.addEventListener('error', () => {
      // An explicit source removal during resize is not a playback failure.
      if (!video.getAttribute('src')) return;
      failed = true;
      showPoster();
      root.setAttribute('data-nwb-sf-fallback', '');
    }, { signal: events.signal });
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      near = entries[0].isIntersecting;
      load();
      update();
    }, { rootMargin: '250% 0px' }) : null;
    if (observer) observer.observe(root); else near = true;
    root.setAttribute('data-nwb-sf-initialized', '');
    frames.set(root, {
      update, configure,
      destroy() {
        disposed = true;
        observer?.disconnect();
        events.abort();
        clearTimeout(seekTimer);
        video.pause();
        video.removeAttribute('src');
        video.load();
        canvas.width = canvas.height = 0;
        root.style.height = originalHeight;
        showPoster();
        ['data-nwb-sf-initialized', 'data-nwb-sf-static', 'data-nwb-sf-progress', 'data-nwb-sf-time', 'data-nwb-sf-fallback'].forEach(name => root.removeAttribute(name));
        frames.delete(root);
      }
    });
    configure();
  }

  function createFade(root) {
    if (fades.has(root)) return;
    const background = root.hasAttribute('data-ndev-sfb');
    const selector = background ? '[data-ndev-sfb-item]' : '[data-ndev-sft-item]';
    const items = [...root.querySelectorAll(selector)].map(item => {
      const variable = background ? (item.dataset.ndevSfbVar || '--ndev-sfb-opacity') : (item.dataset.ndevSftVar || '--ndev-sft-opacity');
      return { item, variable, original: item.style.getPropertyValue(variable) };
    });
    const d = root.dataset;
    function textOpacity(item, headerHeight) {
      const small = innerWidth < 1024 || innerHeight <= 800;
      const top = headerHeight + number(small ? d.ndevSftClipTopMb : d.ndevSftClipTopPc, 0);
      const imageHeight = number(small ? d.ndevSftHeightMb : d.ndevSftHeightPc, small ? 400 : 600);
      const bottomClip = number(small ? d.ndevSftClipBottomMb : d.ndevSftClipBottomPc, 0) || imageHeight;
      const bottom = innerHeight - bottomClip;
      const range = Math.max(0, innerHeight - headerHeight - bottomClip);
      const fadeTop = top + range * number(d.ndevSftFadeTopPct, 20) / 100;
      const fadeBottom = bottom - range * number(d.ndevSftFadeBottomPct, 20) / 100;
      const rect = item.getBoundingClientRect();
      const keepTop = root.hasAttribute('data-ndev-sft-sticky') || !root.hasAttribute('data-ndev-sft-fade-out');
      if (rect.bottom > bottom) return 0;
      if (root.hasAttribute('data-ndev-sft-simple')) return rect.top < top && !keepTop ? 0 : 1;
      if (rect.bottom > fadeBottom) return bottom > fadeBottom ? clamp((bottom - rect.bottom) / (bottom - fadeBottom)) : 1;
      if (rect.top > fadeTop) return 1;
      if (keepTop) return 1;
      return fadeTop > top ? clamp((rect.top - top) / (fadeTop - top)) : 0;
    }
    function update() {
      const headerHeight = document.getElementById('site-header')?.offsetHeight || 0;
      const rect = root.getBoundingClientRect();
      const viewportHeight = Math.max(1, innerHeight - headerHeight);
      const percent = clamp((viewportHeight - rect.top) / (rect.height + viewportHeight)) * 100;
      const start = number(d.ndevSfbFadeStart, 0);
      const end = number(d.ndevSfbFadeEnd, 100);
      items.forEach(({ item, variable }) => {
        if (item.dataset.ndevSftEnabled === 'false') return;
        const opacity = motion.matches ? 1 : background ? (end > start ? clamp((percent - start) / (end - start)) : Number(percent >= end)) : textOpacity(item, headerHeight);
        item.style.setProperty(variable, opacity.toFixed(3));
      });
    }
    root.setAttribute('data-nwb-scroll-fade', '');
    fades.set(root, {
      update,
      destroy() {
        items.forEach(({ item, variable, original }) => {
          if (original) item.style.setProperty(variable, original); else item.style.removeProperty(variable);
        });
        root.removeAttribute('data-nwb-scroll-fade');
        fades.delete(root);
      }
    });
    update();
  }
  function flush() {
    scheduled = 0;
    frames.forEach((controller, root) => root.isConnected ? controller.update() : controller.destroy());
    fades.forEach((controller, root) => root.isConnected ? controller.update() : controller.destroy());
  }
  function schedule() { if (!scheduled) scheduled = requestAnimationFrame(flush); }
  function resize() { frames.forEach(controller => controller.configure()); schedule(); }
  function init(scope = document) {
    visit(scope, '[data-ndev-sf]', createFrame);
    visit(scope, '[data-ndev-sft], [data-ndev-sfb]', createFade);
    schedule();
  }
  function destroy(scope) {
    visit(scope, '[data-ndev-sf]', root => frames.get(root)?.destroy());
    visit(scope, '[data-ndev-sft], [data-ndev-sfb]', root => fades.get(root)?.destroy());
  }
  window.NarwalScroll = { init, destroy, get frameCount() { return frames.size; }, get fadeCount() { return fades.size; } };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', resize, { passive: true });
  document.addEventListener('visibilitychange', schedule);
  motion.addEventListener('change', resize);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
